import React, { useEffect, useMemo, useState } from "react";
import Markdown from "markdown-to-jsx";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ReplayIcon from "@mui/icons-material/Replay";
import { useTranslation } from "react-i18next";

import { copyText, describeStep, headerLine, parseCite, parseTurn } from "./askModel";
import Moments from "./Moments";

const STEP_ICONS = { done: CheckCircleIcon, error: ErrorOutlineIcon, stopped: RemoveCircleOutlineIcon };

function Step({ step }) {
  const { t } = useTranslation();
  const d = describeStep(step, t);
  const Icon = STEP_ICONS[step.status];
  return (
    <li className={"ak-step is-" + step.status}>
      {Icon ? (
        <Icon className="ak-step-icon" />
      ) : (
        <span className="ak-step-icon">
          <CircularProgress size={14} />
        </span>
      )}
      <span className="ak-step-text">{step.status === "running" ? d.doing : d.done}</span>
      {d.detail && <span className="ak-step-detail">{d.detail}</span>}
    </li>
  );
}

// a link of an answer: a moment it cites plays as a time chip, other links open in a new tab
function AnswerLink({ href, children, names, onCite }) {
  const { t } = useTranslation();
  const cite = parseCite(href);
  if (!cite) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  }
  const video = names[cite.projectId];
  const time = React.Children.toArray(children).join("");
  return (
    <a
      className="ak-cite"
      href={href}
      aria-label={video ? t("Play {{video}} at {{time}}", { video, time }) : undefined}
      onClick={(e) => {
        // a click in a new tab or window opens the video's Scenes tab there
        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        onCite(cite);
      }}
    >
      <PlayArrowIcon />
      {children}
    </a>
  );
}

/**
 * An answer: what it did (its steps, open while it works), what it says with the moments it cites, and the moments
 * to play them.
 * @param onPlay (moments, index, highlight) plays one of the answer's moments
 * @param onCite (turn, {projectId, time}) plays a moment it cites
 */
export default function Answer({ message, last, onRetry, onPlay, onCite }) {
  const { t } = useTranslation();
  const status = message.status || "done";
  const running = status === "running";
  const turn = useMemo(() => parseTurn(message.content, running), [message.content, running]);
  const [logOpen, setLogOpen] = useState(null); // null: open while it works, closed after
  const [copied, setCopied] = useState(false);
  const open = logOpen === null ? running : logOpen;
  const header = headerLine(turn, status, t);
  const logId = "ak-log-" + message.index;

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);

  const options = useMemo(
    () => ({
      forceBlock: true,
      // the paragraphs straight in .ak-text, which spaces them
      wrapper: React.Fragment,
      disableParsingRawHTML: true,
      overrides: { a: { component: AnswerLink, props: { names: turn.names, onCite: (cite) => onCite(turn, cite) } } },
    }),
    [turn, onCite],
  );

  const copy = () => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(copyText(turn.text)).then(() => setCopied(true));
  };

  return (
    <article className="ak-answer" aria-label={t("Answer")} aria-busy={running} aria-live={running ? "polite" : undefined}>
      <div className="ak-answer-head">
        <img className="ak-mark" src="/img/favicon.png" alt="" />
        {turn.steps.length > 0 ? (
          <button type="button" className="ak-log-toggle" aria-expanded={open} aria-controls={logId} onClick={() => setLogOpen(!open)}>
            <span>{header}</span>
            {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </button>
        ) : (
          header && <span className="ak-log-toggle">{header}</span>
        )}
      </div>
      {open && turn.steps.length > 0 && (
        <ol id={logId} className="ak-log">
          {turn.steps.map((s, i) => (
            <Step key={i} step={s} />
          ))}
        </ol>
      )}
      {turn.text && (
        <div className="ak-text">
          <Markdown options={options}>{turn.text}</Markdown>
        </div>
      )}
      {status === "error" && (
        <p className="ak-error" role="alert">
          <ErrorOutlineIcon />
          {t(message.error || "Something went wrong: try again.", { nsSeparator: false })}
        </p>
      )}
      {turn.moments.length > 0 && (
        <Moments id={"ak-moments-" + message.index} moments={turn.moments} highlight={turn.highlight} onPlay={(i) => onPlay(turn.moments, i, turn.highlight)} />
      )}
      {!running && (turn.text || last) && (
        <div className="ak-actions">
          {turn.text && (
            <IconButton size="small" title={copied ? t("Copied") : t("Copy")} aria-label={t("Copy")} onClick={copy}>
              <ContentCopyIcon />
            </IconButton>
          )}
          {last && (
            <IconButton size="small" title={t("Retry")} aria-label={t("Retry")} onClick={onRetry}>
              <ReplayIcon />
            </IconButton>
          )}
        </div>
      )}
    </article>
  );
}
